import {Component, OnInit} from '@angular/core';
import {BehaviorSubject, zip} from 'rxjs';
import {combineLatestWith, map, shareReplay, tap} from 'rxjs/operators';

import {ActivatedRoute} from '@angular/router';
import memo from 'memo-decorator';
import {LpInvestingEntityDataService} from 'src/app/services/lp/lp-investing-entity-data.service';
import {ArrayUtils} from 'src/app/shared/utils/array-utils';
import {RoutingService} from '../../../services/routing.service';
import HoldingDiscriminator from '../../../shared/enums/HoldingDiscriminator.enum';
import {LpHoldingPositionRes} from '../../models/LpHoldingPositionRes.model';
import {LpHoldingListItemReqRes} from '../../shared/holdings/LpHoldingListItemReqRes.model';
import {LpCashFlowChartData} from '../../shared/holdings/holding-cash-flow/LpCashFlowChartData.model';
import {LpHoldingService} from '../../shared/holdings/lp-holding.service';

@Component({
  selector: 'terra-lp-holding-information-tab',
  templateUrl: './lp-holding-information-tab.component.html',
  styleUrls: ['./lp-holding-information-tab.component.scss'],
})
export class LpHoldingInformationTabComponent implements OnInit {
  holdingPosition: LpHoldingPositionRes;
  #holdingPositions: LpHoldingPositionRes[] = [];

  holding$ = this.holdingService.holdingDetails$.pipe(
    tap((data) => {
      this.#holdingPositions = data.position;
    }),
    shareReplay(1)
  );

  cashFlowChartData: LpCashFlowChartData;
  // #cashFlowChartData: LpCashFlowChartData[] = [];

  cashFlowChartData$ = this.holdingService.getHoldingCashFlowChartData().pipe(
    tap((chartData) => {
      this.cashFlowChartData = chartData;
      this.filter([]);
    })
    // shareReplay(1)
  );

  investingEntities$ = zip(
    this.lpInvestingEntityDataService.getInvestingEntitiesByCurrentClient(),
    this.lpInvestingEntityDataService.getSharedInvestingEntitiesForCurrentLp()
  ).pipe(
    combineLatestWith(this.holding$),
    map(([[own = [], shared = []], holding]) => {
      const allEntities = [...own];
      let filterEntities = [];

      shared?.forEach((entity) => allEntities.push(entity));
      allEntities.forEach((entity) => {
        if (!filterEntities.some((f) => f.id === entity.id)) {
          filterEntities.push(entity);
        }
      });

      const availableEntities = holding.investingEntitiesIds || [];

      filterEntities = filterEntities.filter((item) =>
        availableEntities.includes(item.id)
      );

      this.filter([], filterEntities);
      return filterEntities;
    })
  );

  displayCapitalInformation$ = new BehaviorSubject<boolean>(true);
  displayAssetDetails$ = new BehaviorSubject<boolean>(true);
  displayProjectInformation$ = new BehaviorSubject<boolean>(true);
  displayMyPosition$ = new BehaviorSubject<boolean>(true);
  displayCashFlowChart$ = new BehaviorSubject<boolean>(false);
  HoldingDiscriminator = HoldingDiscriminator;

  scrollToReport: boolean;

  constructor(
    private holdingService: LpHoldingService,
    private routingService: RoutingService,
    private route: ActivatedRoute,
    private lpInvestingEntityDataService: LpInvestingEntityDataService
  ) {
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const utm_medium = params['utm_medium'];
      if (utm_medium && utm_medium === 'email') {
        this.scrollToReport = true;
      }
    });
  }

  @memo()
  getChildHoldingUrl(holding: LpHoldingListItemReqRes) {
    switch (holding.discriminator) {
      case HoldingDiscriminator.Asset:
        return this.routingService.lpAsset(holding.id);
      case HoldingDiscriminator.Fund:
        return this.routingService.lpFund(holding.id);
    }
  }

  filter(items, allItems = []) {
    const ids = (items.length ? items : allItems)?.map((item) => item.id);
    this.holdingPosition = LpHoldingPositionRes.aggregate(
      this.#holdingPositions.filter((item) =>
        ids?.length ? ids.includes(item.investingEntityId) : true
      )
    );

    this.displayMyPosition$.next(true); // refresh position block

    this.displayCashFlowChart$.next(
      this.cashFlowChartData?.returnOfCapital?.some(
        (roc) => roc.amount > 0
      ) || this.cashFlowChartData?.revenue?.some((r) => r.amount > 0)
    );
  }
}
